import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersService } from '../services/users';

interface User {
  id: string;
  phone: string;
  email?: string;
  role: 'USER' | 'ADMIN' | 'CONTROLLER';
  locale: string;
  createdAt: string;
  updatedAt: string;
  wallet?: {
    id: string;
    balance: number;
    userId: string;
    createdAt: string;
    updatedAt: string;
  };
}

interface UsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  pageSize: number;
}

const initialState: UsersState = {
  users: [],
  isLoading: false,
  error: null,
  total: 0,
  currentPage: 1,
  pageSize: 10,
};

export const fetchUsers = createAsyncThunk(
  'users/fetch',
  async ({ page = 1, pageSize = 10, role = '', search = '' }: { page?: number; pageSize?: number; role?: string; search?: string }) => {
    const response = await usersService.getUsers({ page, pageSize, role, search });
    return (response as any).data;
  }
);

export const updateUser = createAsyncThunk(
  'users/update',
  async ({ id, data }: { id: string; data: Partial<User> }) => {
    const response = await usersService.updateUser(id, data);
    return (response as any).data;
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        // Gérer la structure { data: { users, pagination }, success: true }
        if (!action.payload) {
          console.error('fetchUsers.fulfilled: action.payload is undefined');
          state.error = 'Données non disponibles';
          return;
        }
        
        const response = action.payload.data || action.payload;
        if (!response || !response.users) {
          console.error('fetchUsers.fulfilled: response structure invalid', response);
          state.error = 'Structure de données invalide';
          return;
        }
        
        state.users = response.users;
        state.total = response.pagination?.total || 0;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement';
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
        const index = state.users.findIndex((u: User) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      });
  },
});

export const { setCurrentPage, setPageSize } = usersSlice.actions;
export default usersSlice.reducer;
